import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import { FaPlus, FaClipboardList, FaCheck, FaHourglass, FaExclamationTriangle, FaTrashAlt, FaFileAlt, FaChartLine, FaSync, FaInfoCircle, FaCalendarAlt, FaEdit, FaExchangeAlt, FaChartBar } from 'react-icons/fa';
import NewAssessmentModal from './NewAssessmentModal';
import './Assessment.css';

const AssessmentList = () => {
  const { user } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showNewAssessmentModal, setShowNewAssessmentModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Check if there's a selected organization in localStorage from the dashboard
  const getDashboardState = () => {
    const dashboardState = localStorage.getItem('dashboardState');
    if (dashboardState) {
      try {
        const parsedState = JSON.parse(dashboardState);
        if (parsedState.selectedOrganization && parsedState.selectedOrganization._id) {
          console.log(`Found organization in localStorage: ${parsedState.selectedOrganization.name} (${parsedState.selectedOrganization._id})`);
          return parsedState.selectedOrganization;
        }
      } catch (error) {
        console.error('Error parsing dashboard state:', error);
      }
    }
    return null;
  };

  // Function to fetch assessments - moved outside useEffect to be accessible throughout the component
  const fetchAssessments = async (orgId = null) => {
    try {
      setLoading(true);
      let url = '/api/assessments';
      
      // If we have an organization ID, use it to filter assessments
      if (orgId) {
        url = `/api/assessments?organizationId=${orgId}`;
        console.log(`Fetching assessments for organization: ${orgId}`);
      } else if (user && user.organization) {
        // If no org ID from dashboard but user has an organization, use that
        url = `/api/assessments?organizationId=${user.organization}`;
        console.log(`Fetching assessments for user's organization: ${user.organization}`);
      } else if (user && user.isMultiOrg && user.organizations && user.organizations.length > 0) {
        // For multi-org users without a selected org, use their first organization
        url = `/api/assessments?organizationId=${user.organizations[0]}`;
        console.log(`Fetching assessments for multi-org user's first organization: ${user.organizations[0]}`);
      }
      
      const res = await axios.get(url);
      console.log('Fetched assessments:', res.data);
      
      // Sort assessments alphabetically with Universal at the top
      const sortedAssessments = [...res.data].sort((a, b) => {
        // Pin Universal assessment at the top (case insensitive)
        const aName = a.name ? a.name.toLowerCase() : '';
        const bName = b.name ? b.name.toLowerCase() : '';
        
        if (aName.includes('universal')) return -1;
        if (bName.includes('universal')) return 1;
        
        // Then sort alphabetically by name
        return aName.localeCompare(bName);
      });
      
      console.log('Sorted assessments:', sortedAssessments.map(a => a.name));
      
      setAssessments(sortedAssessments);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching assessments:', err);
      setError('Failed to load assessments');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Get the organization from localStorage first
    const org = getDashboardState();
    if (org && org._id) {
      setSelectedOrganization(org);
      console.log(`Using organization from localStorage: ${org.name} (${org._id})`);
      fetchAssessments(org._id);
    } else {
      console.log('No organization found in localStorage, using default filtering');
      fetchAssessments();
    }
    
    // Add event listener for storage changes to detect organization changes
    const handleStorageChange = () => {
      console.log('Storage changed, checking for organization updates');
      const updatedOrg = getDashboardState();
      if (updatedOrg && updatedOrg._id && 
          (!selectedOrganization || updatedOrg._id !== selectedOrganization._id)) {
        console.log(`Organization changed to: ${updatedOrg.name}`);
        fetchAssessments(updatedOrg._id);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    }
  }, [user]);
  
  // Regenerate scorecard for an assessment
  const regenerateScorecard = async (id) => {
    try {
      setLoading(true);
      setError('');
      
      // Get the organization ID to filter assessments after regeneration
      let orgId = null;
      if (selectedOrganization && selectedOrganization._id) {
        orgId = selectedOrganization._id;
      } else if (user && user.organization) {
        orgId = typeof user.organization === 'object' ? user.organization._id : user.organization;
      }
      
      console.log(`Regenerating scorecard for assessment ID: ${id} with organization ID: ${orgId || 'none'}`);
      
      // Get token directly from localStorage to ensure it's fresh
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Use a direct fetch call to avoid any issues with axios interceptors
      console.log(`Making API call to: /api/assessments/${id}/regenerate-scorecard`);
      
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? `${window.location.origin}/api/assessments/${id}/regenerate-scorecard` 
        : `http://localhost:5000/api/assessments/${id}/regenerate-scorecard`;
      
      const queryString = orgId ? `?organizationId=${orgId}` : '';
      const fullUrl = `${apiUrl}${queryString}`;
      
      console.log(`Full URL: ${fullUrl}`);
      
      const response = await fetch(fullUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({}) // empty body
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Scorecard regeneration successful:', data);
      
      setAssessments(data);
      setSuccess('Scorecard regenerated successfully');
      setTimeout(() => setSuccess(''), 3000);
      setLoading(false);
    } catch (err) {
      console.error('Error regenerating scorecard:', err);
      // Don't log out on error, just show error message
      setError('Failed to regenerate scorecard: ' + (err.message || 'Unknown error'));
      setTimeout(() => setError(''), 5000);
      setLoading(false);
      
      // Refresh the assessment list to ensure UI stays in sync
      // Get the organization ID for refreshing the list
      let refreshOrgId = null;
      if (selectedOrganization && selectedOrganization._id) {
        refreshOrgId = selectedOrganization._id;
      }
      fetchAssessments(refreshOrgId);
    }
  };

  // Delete an assessment
  const deleteAssessment = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`/api/assessments/${id}`);
      
      // Remove the assessment from the state
      setAssessments(assessments.filter(assessment => assessment._id !== id));
      setSuccess('Assessment deleted successfully');
      setDeleteConfirm(null);
      setLoading(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting assessment:', err);
      setError('Failed to delete assessment');
      setLoading(false);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    }
  };
  
  // Calculate completion percentage based on responses
  const calculateCompletion = (assessment) => {
    if (!assessment.responses || assessment.responses.length === 0) return 0;
    const answered = assessment.responses.filter(r => r.answer && r.answer !== '').length;
    return Math.round((answered / assessment.responses.length) * 100);
  };

  // Determine if assessment is a DORA assessment
  const isDORAAssessment = (assessment) => {
    // Check various properties that might indicate this is a DORA assessment
    if (assessment.templateName && assessment.templateName.includes('DORA')) return true;
    if (assessment.description && assessment.description.includes('DORA')) return true;
    if (assessment.title && assessment.title.includes('DORA')) return true;
    if (assessment.templateId && assessment.templateId.includes('DORA')) return true;
    
    // Check if any question in the assessment mentions DORA
    if (assessment.responses && assessment.responses.some(r => 
      (r.question && r.question.includes('DORA')) || 
      (r.category && r.category.includes('DORA')))) {
      return true;
    }
    
    return false;
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'Poppins, sans-serif'
    },
    assessmentGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '25px',
      marginTop: '20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px'
    },
    title: {
      margin: 0,
      color: '#243444',
      fontSize: '24px'
    },
    subtitle: {
      margin: '0 0 20px 0',
      color: '#4C5454',
      fontSize: '16px'
    },
    button: {
      backgroundColor: '#243444',
      color: '#FFFAFA',
      border: 'none',
      padding: '10px 16px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      fontFamily: 'Poppins, sans-serif',
      fontSize: '15px',
      justifyContent: 'center',
      flex: 1
    },
    deleteButton: {
      backgroundColor: '#7C1227',
      color: '#FFFAFA'
    },
    cancelButton: {
      backgroundColor: '#4C5454',
      color: '#FFFAFA'
    },
    assessmentItem: {
      backgroundColor: '#FFFAFA',
      borderRadius: '12px',
      boxShadow: '0 3px 10px rgba(0,0,0,0.05)'
    },
    card: {
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
      border: '1px solid rgba(0,0,0,0.05)',
      position: 'relative',
      width: '100%',
      fontFamily: 'Poppins, sans-serif',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '20px',
      minHeight: '320px'
    },
    cardHover: {
      transform: 'translateY(-5px)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.12)'
    },
    cardHeader: {
      backgroundColor: '#243444',
      color: '#D2BA7B',
      padding: '16px 20px',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative',
      zIndex: 1
    },
    cardTitle: {
      margin: 0,
      fontSize: '18px',
      fontWeight: '600',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: '1',
      WebkitBoxOrient: 'vertical',
      width: '100%',
      color: '#D2BA7B',
      fontFamily: 'Poppins, sans-serif'
    },
    cardContent: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#FFFAFA',
      overflow: 'auto',
      justifyContent: 'space-between',
      gap: '15px',
      flex: 1
    },
    description: {
      fontSize: '16px',
      color: '#4C5454',
      marginBottom: '15px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: '2',
      WebkitBoxOrient: 'vertical',
      lineHeight: '1.5',
      fontFamily: 'Poppins, sans-serif',
      fontWeight: '400'
    },
    metaSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      marginBottom: '15px'
    },
    metaLabel: {
      fontWeight: '600',
      color: '#4C5454',
      fontSize: '15px',
      marginRight: '8px',
      fontFamily: 'Poppins, sans-serif',
      display: 'inline-block',
      width: '110px'
    },
    metaValue: {
      color: '#4C5454',
      fontSize: '15px',
      fontFamily: 'Poppins, sans-serif',
      fontWeight: '500'
    },
    cardFooter: {
      padding: '15px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: '#FFFAFA',
      marginTop: 'auto',
      gap: '10px'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 30px',
      backgroundColor: '#FFFAFA',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      textAlign: 'center',
      fontFamily: 'Poppins, sans-serif'
    },
    emptyIcon: {
      fontSize: '48px',
      color: '#D2BA7B',
      marginBottom: '15px'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      width: '400px',
      maxWidth: '90%',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
    },
    modalTitle: {
      color: '#243444',
      marginTop: 0
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '20px'
    },
    backLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      color: '#243444',
      textDecoration: 'none',
      padding: '8px 10px',
      borderRadius: '4px',
      transition: 'background-color 0.2s ease',
      fontSize: '14px'
    },
    deleteIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#7C1227',
      fontSize: '18px',
      transition: 'color 0.2s ease',
      padding: '10px',
      borderRadius: '6px',
      backgroundColor: 'rgba(124, 18, 39, 0.1)'
    },
    reportIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#243444',
      fontSize: '18px',
      transition: 'color 0.2s ease',
      padding: '10px',
      borderRadius: '6px',
      backgroundColor: 'rgba(36, 52, 68, 0.1)'
    },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '18px',
      transition: 'all 0.2s ease',
      padding: '10px',
      borderRadius: '6px',
      width: '40px',
      height: '40px',
      color: '#243444'
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '6px 10px',
      borderRadius: '6px'
    },
    progressText: {
      fontSize: '14px',
      color: '#FFFAFA',
      fontFamily: 'Poppins, sans-serif'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: 'Poppins, sans-serif',
      marginBottom: '15px'
    },
    completed: {
      backgroundColor: 'rgba(210, 186, 123, 0.15)',
      color: '#D2BA7B',
      border: '1px solid #D2BA7B'
    },
    inProgress: {
      backgroundColor: 'rgba(76, 84, 84, 0.1)',
      color: '#4C5454',
      border: '1px solid #4C5454'
    },

  };



  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Assessments</h1>
        <button 
          style={{
            ...styles.button,
            padding: '8px 12px',
            fontSize: '14px',
            flex: 'none'
          }}
          onClick={() => setShowNewAssessmentModal(true)}
        >
          <FaPlus style={{marginRight: '5px'}} /> Create Assessment
        </button>
      </div>
      <p style={styles.subtitle}>View and manage your resilience assessments</p>
      
      {/* Success message */}
      {success && (
        <div style={styles.successMessage}>
          <FaCheck /> {success}
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div style={styles.errorMessage}>
          <FaExclamationTriangle /> {error}
        </div>
      )}
      
      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Delete Assessment</h3>
            <p>Are you sure you want to delete this assessment? This action cannot be undone.</p>
            <div style={styles.modalButtons}>
              <button 
                style={{...styles.button, ...styles.cancelButton}}
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                style={{...styles.button, ...styles.deleteButton}}
                onClick={() => deleteAssessment(deleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {loading ? (
        <div style={styles.loading}>
          <FaSync className="spin" /> Loading assessments...
        </div>
      ) : assessments.length === 0 ? (
        <div style={styles.emptyState}>
          <FaClipboardList style={styles.emptyIcon} />
          <h3 style={{fontFamily: 'Poppins, sans-serif', color: '#243444', margin: '0 0 10px 0'}}>No Assessments Found</h3>
          <p style={{fontFamily: 'Poppins, sans-serif', color: '#4C5454'}}>You haven't created any assessments yet. Get started by creating your first assessment.</p>
          <button 
            style={styles.button}
            onClick={() => setShowNewAssessmentModal(true)}
          >
            <FaPlus /> Create Assessment
          </button>
        </div>
      ) : (
        <div style={styles.assessmentGrid}>
          {assessments.map(assessment => {
            // Format date
            const lastModified = new Date(assessment.updatedAt).toLocaleDateString();
            
            // Format due date if exists
            const dueDate = assessment.dueDate 
              ? new Date(assessment.dueDate).toLocaleDateString() 
              : 'Not set';
              
            // Calculate completion percentage
            const completionPercentage = calculateCompletion(assessment);
              
            return (
              <div key={assessment._id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>
                    {isDORAAssessment(assessment) ? 'DORA Assessment' : 
                     (assessment.templateName ? 
                      assessment.templateName : 
                      (assessment.region && assessment.region.toLowerCase() !== 'general' ? 
                       `${assessment.region} ${assessment.industryType} Assessment` : 
                       `${assessment.industryType} Assessment`))}
                  </h3>
                </div>
                
                <div style={styles.cardContent}>
                  <div>
                    <div style={{
                      ...styles.statusBadge,
                      ...(assessment.status === 'completed' ? styles.completed : styles.inProgress)
                    }}>
                      {assessment.status === 'completed' ? (
                        <>
                          <FaCheck /> Completed
                        </>
                      ) : (
                        <>
                          <FaHourglass /> In Progress ({completionPercentage}%)
                        </>
                      )}
                    </div>
                    
                    <p style={styles.description}>
                      Assessment of {assessment.organizationName || (selectedOrganization && selectedOrganization.name) || 'Organization'}'s {assessment.industryType} resilience
                    </p>
                  </div>
                  
                  <div style={styles.metaSection}>
                    <div>
                      <span style={styles.metaLabel}>Organization:</span>
                      <span style={styles.metaValue}>{assessment.organizationName || (selectedOrganization && selectedOrganization.name) || 'Organization'}</span>
                    </div>
                    <div>
                      <span style={styles.metaLabel}>Industry:</span>
                      <span style={styles.metaValue}>{assessment.industryType}</span>
                    </div>

                    <div>
                      <span style={styles.metaLabel}>Created:</span>
                      <span style={styles.metaValue}>{lastModified}</span>
                    </div>
                    {assessment.status === 'completed' && (
                      <div>
                        <span style={styles.metaLabel}>Score:</span>
                        <span style={styles.metaValue}>{assessment.score || '100'}%</span>
                      </div>
                    )}
                  </div>
                  
                  <div style={{...styles.cardFooter, flexWrap: 'wrap', gap: '10px'}}>
                    <div style={{display: 'flex', gap: '10px', width: '100%', justifyContent: 'space-between'}}>
                      <Link 
                        to={`/assessments/${assessment._id}`} 
                        style={{...styles.button, flex: '1'}}
                      >
                        {assessment.status === 'completed' ? 'View Results' : 'Continue Assessment'}
                      </Link>
                      
                      {assessment.status === 'completed' && (
                        <Link 
                          to={`/assessments/${assessment._id}/edit`} 
                          style={{
                            ...styles.button,
                            backgroundColor: '#4C5454',
                            flex: '1'
                          }}
                          title="Reopen to update answers"
                        >
                          <FaEdit style={{marginRight: '5px'}} /> Reopen
                        </Link>
                      )}
                    </div>
                    
                    <div style={{display: 'flex', gap: '10px', justifyContent: 'center', width: '100%'}}>
                      {assessment.status === 'completed' && (
                        <div 
                          style={{...styles.iconButton, backgroundColor: 'rgba(210, 186, 123, 0.1)', color: '#D2BA7B'}} 
                          onClick={(e) => {
                            e.stopPropagation();
                            regenerateScorecard(assessment._id);
                          }}
                          title="Regenerate Scorecard"
                        >
                          <FaChartBar />
                        </div>
                      )}
                      <div 
                        style={{...styles.iconButton, backgroundColor: 'rgba(124, 18, 39, 0.1)', color: '#7C1227'}} 
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(assessment._id);
                        }}
                        title="Delete Assessment"
                      >
                        <FaTrashAlt />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div style={{display: 'flex', justifyContent: 'center', padding: '5px 0 15px'}}>
                  <div style={{color: '#4C5454', fontSize: '14px', fontFamily: 'Poppins, sans-serif'}}>
                    {assessment.templateName || `${assessment.industryType} Assessment`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* New Assessment Modal */}
      <NewAssessmentModal 
        isOpen={showNewAssessmentModal} 
        onClose={() => {
          setShowNewAssessmentModal(false);
          // Refresh assessments when modal is closed
          const fetchAssessments = async () => {
            try {
              setLoading(true);
              setError(null);
              
              if (!user) {
                console.log('No user data available');
                setLoading(false);
                return;
              }
              
              // Get the current organization
              let orgId;
              
              if (selectedOrganization?._id) {
                // Use currently selected organization
                orgId = selectedOrganization._id;
                console.log(`Using selected organization: ${selectedOrganization.name} (${orgId})`);
              } else {
                // Try to get organization from localStorage
                const lastSelectedOrgData = localStorage.getItem('lastSelectedOrganization');
                if (lastSelectedOrgData) {
                  const lastSelectedOrg = JSON.parse(lastSelectedOrgData);
                  orgId = lastSelectedOrg._id;
                  console.log(`Using organization from localStorage: ${lastSelectedOrg.name} (${orgId})`);
                } else if (user.organization) {
                  // Use user's assigned organization
                  orgId = typeof user.organization === 'object' ? user.organization._id : user.organization;
                  console.log(`Using user's assigned organization: ${orgId}`);
                }
              }
              
              if (!orgId) {
                console.error('No organization ID available');
                setError('No organization selected');
                setLoading(false);
                return;
              }
              
              // Fetch assessments for the organization
              const url = `/api/assessments?organizationId=${orgId}`;
              console.log(`Fetching assessments from: ${url}`);
              
              const res = await axios.get(url);
              setAssessments(res.data);
              setLoading(false);
            } catch (err) {
              console.error('Error fetching assessments:', err);
              setError('Failed to load assessments');
              setLoading(false);
            }
          };
          fetchAssessments();
        }} 
      />
    </div>
  );
};

export default AssessmentList;
